import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import config from "../config";
import WorkerSidebarShell from "./WorkerSidebarShell";

const RemoteProfileView = () => {
  const navigate = useNavigate();
  const workerID = localStorage.getItem("workerID");

  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!workerID) {
      setLoading(false);
      return;
    }

    axios
      .get(`${config.API_BASE_URL}${config.WORKER.PROFILE}/${workerID}`)
      .then((res) => setProfile(res.data))
      .catch(() => setProfile(null))
      .finally(() => setLoading(false));
  }, [workerID]);

  if (loading) return <p style={{ padding: 40 }}>Loading profile...</p>;

  if (!profile) {
    return (
      <WorkerSidebarShell activeItem="profile">
        <div style={fallbackCard}>
          <h2 style={title}>Profile Not Found</h2>
          <p style={fallbackText}>Complete your profile setup to continue.</p>
          <button style={primaryBtn} onClick={() => navigate("/remoteProfileEdit")}>
            Create Profile
          </button>
        </div>
      </WorkerSidebarShell>
    );
  }

  return (
    <WorkerSidebarShell activeItem="profile">
      <div style={card}>
        <div style={headerActionRow}>
          <h2 style={title}>Worker Profile</h2>
          <button style={editBtnSmall} onClick={() => navigate("/remoteProfileEdit")}>
            Edit Profile
          </button>
        </div>

        <div style={profileHeader}>
          <div style={avatarCircle}>
            {profile.firstName ? profile.firstName[0].toUpperCase() : "W"}
          </div>
          <div>
            <h1 style={profileName}>
              {profile.firstName || "Worker"} {profile.lastName || ""}
            </h1>
            <p style={profileSub}>
              {profile.city ? `${profile.city}, ` : ""}
              {profile.country || "Location not set"}
            </p>
          </div>
        </div>

        <div style={gridContainer}>
          <Section title="Core Profile">
            <Row label="First Name" value={profile.firstName} />
            <Row label="Last Name" value={profile.lastName} />
            <Row label="Spoken Language" value={profile.spokenLanguage} />
            <Row label="Gender" value={profile.gender === "M" ? "Male" : profile.gender === "F" ? "Female" : profile.gender} />
            <Row label="Country" value={profile.country} />
            <Row label="City" value={profile.city} />
          </Section>

          <Section title="Work Details">
            <Row label="WhatsApp" value={profile.whatsapp} />
            <Row label="Hourly Rate" value={profile.rate ? `$${profile.rate}/hr` : ""} />
            <Row label="Availability Status" value={profile.status || "available"} />
          </Section>
        </div>

        <div style={actionRow}>
          <button style={secondaryBtn} onClick={() => navigate("/worker-home")}>
            Back to Dashboard
          </button>
        </div>
      </div>
    </WorkerSidebarShell>
  );
};

export default RemoteProfileView;

const Section = ({ title, children }) => (
  <div style={section}>
    <h3 style={sectionTitle}>{title}</h3>
    <div style={sectionContent}>{children}</div>
  </div>
);

const Row = ({ label, value }) => {
  if (value === undefined || value === null || value === "") return null;
  return (
    <div style={rowContainer}>
      <div style={rowLabel}>{label}</div>
      <div style={rowValue}>{String(value)}</div>
    </div>
  );
};

const card = {
  background: "#fff",
  padding: "32px",
  width: "100%",
  maxWidth: "900px",
  borderRadius: "12px",
  boxShadow: "0 4px 16px rgba(0, 0, 0, 0.06)",
  margin: "0 auto",
  boxSizing: "border-box"
};

const fallbackCard = {
  ...card,
  maxWidth: "640px"
};

const headerActionRow = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  marginBottom: "30px",
  borderBottom: "1px solid #edf2f7",
  paddingBottom: "16px"
};

const title = {
  color: "#0E5580",
  margin: 0,
  fontSize: "24px",
  fontWeight: 600
};

const editBtnSmall = {
  padding: "8px 16px",
  background: "#ffffff",
  color: "#0E5580",
  border: "1px solid #0E5580",
  borderRadius: "8px",
  cursor: "pointer",
  fontSize: "14px"
};

const profileHeader = {
  display: "flex",
  alignItems: "center",
  gap: "20px",
  marginBottom: "28px"
};

const avatarCircle = {
  width: "88px",
  height: "88px",
  borderRadius: "50%",
  background: "#0E5580",
  color: "#fff",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  fontSize: "34px",
  fontWeight: 700
};

const profileName = {
  margin: "0 0 4px 0",
  color: "#12344d",
  fontSize: "24px",
  fontWeight: 600
};

const profileSub = {
  margin: 0,
  color: "#6b8396",
  fontSize: "15px"
};

const gridContainer = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
  gap: "24px"
};

const section = {
  background: "#f9fcff",
  borderRadius: "12px",
  border: "1px solid #eef3f7",
  padding: "20px",
  boxSizing: "border-box"
};

const sectionTitle = {
  color: "#0E5580",
  fontSize: "18px",
  fontWeight: 600,
  margin: "0 0 16px 0",
  paddingBottom: "8px",
  borderBottom: "1px solid #e1ebf2"
};

const sectionContent = {
  display: "flex",
  flexDirection: "column",
  gap: "14px"
};

const rowContainer = {
  display: "flex",
  flexDirection: "column",
  gap: "4px"
};

const rowLabel = {
  fontSize: "14px",
  color: "#4f6475",
  fontWeight: 600
};

const rowValue = {
  fontSize: "15px",
  color: "#234159",
  fontWeight: 500
};

const actionRow = {
  display: "flex",
  justifyContent: "flex-end",
  marginTop: "28px",
  paddingTop: "20px",
  borderTop: "1px solid #edf2f7"
};

const secondaryBtn = {
  padding: "12px 24px",
  background: "#ffffff",
  color: "#0E5580",
  border: "1px solid #0E5580",
  borderRadius: "8px",
  cursor: "pointer",
  fontSize: "15px"
};

const primaryBtn = {
  padding: "12px 18px",
  background: "#0E5580",
  color: "#fff",
  border: "none",
  borderRadius: "8px",
  cursor: "pointer",
  fontSize: "15px"
};

const fallbackText = {
  color: "#4f6475",
  marginBottom: "20px"
};
